import { CustomFieldDefinitionActionAvailability } from '@/api/queries/customField/useGetFieldset';
import {
  FormValues,
  TCreateEditCustomFieldSidebarContentProps,
} from '@/ui/components/customFieldGroupControl/CreateEditCustomFieldSidebar';
import {
  Field,
  Input,
  NumberInput,
  RadioGroupField,
  Select,
  Textarea,
} from '@payaca/ambrosia';

import CustomFieldGroupOptionsInput from '@/ui/components/customFieldGroupControl/components/CustomFieldGroupOptionsInput';

import { MAX_UPLOAD_LIMIT } from '@payaca/custom-fields/constants/index';
import { camelize } from '@payaca/utilities/stringUtilities';
import { FC } from 'react';
import { Controller, useFormContext } from 'react-hook-form';
import { v4 as uuid } from 'uuid';
import { appendPath, PathToFieldsetChild } from '../types';
import { getDepthFromFieldPath } from '../utils/getDepthFromFieldPath';
import { getTypeOptionsForField } from '../utils/getTypeOptionsForField';
import NestedFieldsetChildrenEditor from './NestedFieldsetChildrenEditor';
import { useFieldsetChildField } from './useFieldsetChildField';

export type TProps = {
  isDestructivelyUpdateable: CustomFieldDefinitionActionAvailability['isDestructivelyUpdateable'];
  isPersisted: boolean;
  /** Field path for nested fields (e.g., "children.0" or "children.0.children.1"). Undefined for root field. */
  fieldPath?: PathToFieldsetChild;
  /** Callback to open the nested field editor sidebar (for depth 2 fields) */
  onOpenNestedFieldEditor?: (parentIndex: number, nestedIndex: number) => void;
  /** Whether the nested field editor sidebar is currently open */
  isNestedFieldEditorOpen?: boolean;
  /** Full action availability array for per-child removal checks in nested fieldsets */
  actionAvailability?: CustomFieldDefinitionActionAvailability[];
  /** Root fieldset identifier (e.g. "projects") */
  fieldsetIdentifier?: string;
} & Pick<TCreateEditCustomFieldSidebarContentProps, 'allCurrentIdentifiers'>;

const CreateEditCustomFieldFieldset: FC<TProps> = (props) => {
  const {
    isDestructivelyUpdateable,
    allCurrentIdentifiers,
    fieldPath,
    isPersisted,
    onOpenNestedFieldEditor,
    isNestedFieldEditorOpen,
    actionAvailability,
    fieldsetIdentifier,
  } = props;

  const depth = getDepthFromFieldPath(fieldPath);

  const { control, watch, setError, clearErrors } =
    useFormContext<FormValues>();

  const type = watch(appendPath(fieldPath, 'type'));

  const typeOptions = getTypeOptionsForField(depth, isPersisted);

  return (
    <>
      <Controller
        render={({ field: { value, onChange, name } }) => {
          const identificationValue = value;
          return (
            <>
              <Field name={`${name}.label`}>
                <Field.Label>Label</Field.Label>

                <Input
                  value={identificationValue?.label}
                  onChange={(newLabel) => {
                    if (isPersisted) {
                      onChange({ ...identificationValue, label: newLabel });
                      return;
                    }

                    let newIdentifier = camelize(
                      newLabel.replace(/[^a-zA-Z0-9 ]/g, '')
                    ).slice(0, 250);

                    if (allCurrentIdentifiers.includes(newIdentifier)) {
                      newIdentifier = `${newIdentifier}${uuid().slice(0, 5)}`;
                    }

                    onChange({
                      identifier: newIdentifier,
                      label: newLabel,
                    });
                  }}
                />
              </Field>

              <Field name={`${name}.identifier`}>
                <Field.Label>Internal name</Field.Label>
                <Field.HelperText>
                  This unique identifier will be used for integrations.
                </Field.HelperText>

                <Input
                  disabled={isPersisted}
                  value={identificationValue?.identifier}
                  onChange={(v) => {
                    if (allCurrentIdentifiers.includes(v)) {
                      setError(
                        appendPath(fieldPath, 'identification.identifier'),
                        {
                          type: 'custom',
                          message: 'This identifier is already in use',
                        }
                      );
                    } else {
                      clearErrors(
                        appendPath(fieldPath, 'identification.identifier')
                      );
                    }

                    onChange({ ...identificationValue, identifier: v });
                  }}
                />
              </Field>
            </>
          );
        }}
        name={appendPath(fieldPath, 'identification')}
        control={control}
      />

      <Controller
        render={({ field: { value, onChange, name } }) => {
          return (
            <Field name={name}>
              <Field.Label>Description</Field.Label>
              <Field.HelperText>
                This description will guide users on how to use this field.
              </Field.HelperText>
              <Textarea
                value={value ?? undefined}
                onChange={onChange}
                rows={1}
                autoHeight={true}
              />
            </Field>
          );
        }}
        name={appendPath(fieldPath, 'description')}
        control={control}
      />

      {!!typeOptions.length && fieldPath && (
        <Controller
          name={fieldPath}
          control={control}
          render={({ field: { value, onChange, name } }) => {
            const fieldValue = value;
            return (
              <Field name={`${name}.type`}>
                <Field.Label>Type</Field.Label>
                <Select
                  disabled={
                    !isDestructivelyUpdateable.result ||
                    (isPersisted && fieldValue.type === 'fieldset')
                  }
                  options={typeOptions}
                  value={fieldValue.type}
                  onChange={(newType) => {
                    const baseValue = {
                      identification: fieldValue.identification,
                      description: fieldValue.description,
                      type: newType,
                    };

                    switch (newType) {
                      case 'select':
                        onChange({
                          ...baseValue,
                          options: [],
                          displayMode: 'dropdown',
                        });
                        break;
                      case 'upload':
                        onChange({
                          ...baseValue,
                          limit: 1,
                        });
                        break;
                      case 'fieldset':
                        onChange({
                          ...baseValue,
                          children: [],
                        });
                        break;
                      default:
                        onChange(baseValue);
                        break;
                    }
                  }}
                />
              </Field>
            );
          }}
        />
      )}

      {type === 'select' && (
        <AdditionalSelectInput
          isDestructivelyUpdateable={isDestructivelyUpdateable}
          allCurrentIdentifiers={allCurrentIdentifiers}
          fieldPath={fieldPath}
          isPersisted={isPersisted}
        />
      )}

      {type === 'upload' && (
        <AdditionalUploadInput
          isDestructivelyUpdateable={isDestructivelyUpdateable}
          allCurrentIdentifiers={allCurrentIdentifiers}
          fieldPath={fieldPath}
          isPersisted={isPersisted}
        />
      )}

      {type === 'fieldset' && !!fieldPath && (
        <NestedFieldsetChildrenEditor
          parentFieldName={fieldPath}
          isPersisted={isPersisted}
          actionAvailability={actionAvailability}
          fieldsetIdentifier={fieldsetIdentifier}
          parentFieldIdentifier={
            watch(appendPath(fieldPath, 'identification'))?.identifier || ''
          }
          onOpenNestedFieldEditor={onOpenNestedFieldEditor}
          isNestedFieldEditorOpen={isNestedFieldEditorOpen}
        />
      )}
    </>
  );
};

export default CreateEditCustomFieldFieldset;

const AdditionalSelectInput: FC<TProps> = (props) => {
  const { isDestructivelyUpdateable, fieldPath } = props;

  const fieldsetChildField = useFieldsetChildField({
    fieldPath,
    expectedType: 'select',
  });

  if (!fieldsetChildField) return null;

  const { namePrefix, control, initialValue } = fieldsetChildField;

  const initialOptions =
    initialValue?.type === 'select'
      ? (initialValue.options?.filter((x) => !!x) as string[] | undefined)
      : undefined;

  return (
    <>
      <Controller
        name={`${namePrefix}.options`}
        control={control}
        render={({ field }) => {
          return (
            <Field name={`${namePrefix}.options`}>
              <Field.Label>Options</Field.Label>

              <CustomFieldGroupOptionsInput
                preventDeleteOptions={
                  isDestructivelyUpdateable.result ? [] : initialOptions
                }
                options={field.value}
                onChange={(newOptions) => field.onChange(newOptions)}
              />
            </Field>
          );
        }}
      />

      <Controller
        name={`${namePrefix}.displayMode`}
        control={control}
        render={({ field: { value, onChange, name } }) => {
          return (
            <Field name={name}>
              <Field.Label>Display mode</Field.Label>
              <Field.HelperText>
                How this field is displayed when filling in the form.
              </Field.HelperText>
              <RadioGroupField
                horizontal
                options={[
                  { label: 'Dropdown', value: 'dropdown' },
                  { label: 'Buttons', value: 'radio' },
                ]}
                value={value}
                onChange={onChange}
              />
            </Field>
          );
        }}
      />
    </>
  );
};

const AdditionalUploadInput: FC<TProps> = (props) => {
  const { fieldPath } = props;

  const fieldsetChildField = useFieldsetChildField({
    fieldPath,
    expectedType: 'upload',
  });

  if (!fieldsetChildField) return null;

  const { namePrefix, control } = fieldsetChildField;

  return (
    <Controller
      name={appendPath(namePrefix, 'limit')}
      control={control}
      render={({ field }) => {
        return (
          <Field name={appendPath(namePrefix, 'limit')}>
            <Field.Label>Upload limit</Field.Label>
            <Field.HelperText>
              Number of files that can be uploaded to this field (max{' '}
              {MAX_UPLOAD_LIMIT})
            </Field.HelperText>
            <NumberInput
              value={field.value ?? null}
              onChange={(newLimit) => field.onChange(newLimit || undefined)}
            />
          </Field>
        );
      }}
    />
  );
};
